export function shouldPauseCreatorVideos(state) {
  return Boolean(!state.sectionVisible || !state.documentVisible);
}

function isMobileMedia() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function syncVideoPoster(video) {
  if (!video) return;

  const host = video.closest('[data-responsive-video-poster]');
  if (!host) return;

  const poster = isMobileMedia()
    ? host.dataset.mobilePoster || host.dataset.poster || ''
    : host.dataset.poster || '';

  if (poster && video.getAttribute('poster') !== poster) {
    video.setAttribute('poster', poster);
  }
}

const elementName = 'aceii-creator-reactions';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiCreatorReactions extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.cards = [...this.querySelectorAll('[data-creator-card]')];
      this.sectionVisible = false;
      this.documentVisible = !document.hidden;
      const { signal } = this.abortController;

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        const control = card.querySelector('[data-creator-toggle]');
        if (!video || !control) return;

        syncVideoPoster(video);

        control.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.toggleVideo(card, video);
          },
          { signal },
        );
        card.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('video, button')) return;
            this.toggleVideo(card, video);
          },
          { signal },
        );
        video.addEventListener(
          'play',
          () => {
            this.pauseAll(video);
            this.updateCard(card, video);
          },
          { signal },
        );
        video.addEventListener(
          'pause',
          () => this.updateCard(card, video),
          { signal },
        );
        video.addEventListener(
          'ended',
          () => {
            video.currentTime = 0;
            this.updateCard(card, video);
          },
          { signal },
        );
      });

      window.addEventListener(
        'resize',
        () => this.querySelectorAll('video').forEach(syncVideoPoster),
        { signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          if (
            shouldPauseCreatorVideos({
              sectionVisible: this.sectionVisible,
              documentVisible: this.documentVisible,
            })
          ) {
            this.pauseAll();
          }
        },
        { signal },
      );

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.sectionVisible = Boolean(entry?.isIntersecting);
          if (this.sectionVisible) {
            this.ensureSources();
          } else {
            this.pauseAll();
          }
        },
        {
          rootMargin: '200px 0px',
          threshold: 0.01,
        },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.pauseAll();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    ensureSources() {
      this.querySelectorAll('video[data-src]').forEach((video) => {
        syncVideoPoster(video);
        if (video.currentSrc || video.src) return;
        video.src = video.dataset.src;
        video.load();
      });
    }

    async toggleVideo(card, video) {
      if (video.paused) {
        this.ensureSources();
        this.pauseAll(video);
        video.controls = true;

        try {
          await video.play();
          this.removeAttribute('data-playback-blocked');
        } catch {
          video.controls = false;
          this.setAttribute('data-playback-blocked', 'true');
          this.updateCard(card, video);
        }
      } else {
        video.pause();
      }
    }

    pauseAll(exceptVideo = null) {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video || video === exceptVideo) return;
        if (!video.paused) video.pause();
        video.controls = false;
        this.updateCard(card, video);
      });
    }

    updateCard(card, video) {
      const playing = !video.paused && !video.ended;
      const control = card.querySelector('[data-creator-toggle]');

      card.classList.toggle('is-playing', playing);
      video.controls = playing;

      if (control) {
        control.setAttribute('aria-pressed', String(playing));
        control.setAttribute(
          'aria-label',
          playing ? 'Pause creator video' : 'Play creator video',
        );
      }
    }
  }

  window.customElements.define(elementName, AceiiCreatorReactions);
}
